import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import useBrandIdentity from "../hooks/useBrandIdentity";
import { Button } from "./ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "./ui/dropdown-menu";
import { Wallet, LogOut, Store, Shield, LogIn, Menu, X, Home, Tag, Compass, FileText, Settings, Image as ImageIcon, Star, MapPin, ShoppingBag, Globe, ShoppingCart, ChevronDown, Building2, Landmark, CheckCircle2, Users } from "lucide-react";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { itemCount } = useCart() || {};
  const { logo: LOGO_URL } = useBrandIdentity();

  const close = () => setOpen(false);

  const navItems = [
    { to: "/", label: "Início", icon: Home, public: true, end: true },
    { to: "/discover/all", label: "Descobrir", icon: Compass, public: true },
    { to: "/cidades", label: "Cidades", icon: MapPin, public: true },
    { to: "/shop", label: "Shop", icon: ShoppingBag, public: true },
    { to: "/blog", label: "Blog", icon: FileText, public: true },
    { to: "/pricing", label: "Planos", icon: Tag, anyRole: ["ADMIN", "MERCHANT"], publicForAnon: true },
    { to: "/para-empresas", label: "Quero cadastrar minha empresa", icon: Store, anyRole: ["CUSTOMER"], publicForAnon: true },
    { to: "/wallet", label: "Carteira", icon: Wallet, roles: ["CUSTOMER", "ADMIN"] },
    { to: "/orders", label: "Minhas Compras", icon: ShoppingBag, roles: ["CUSTOMER"] },
    { to: "/vouchers", label: "Meus Vouchers", icon: Tag, roles: ["CUSTOMER"] },
    { to: "/profile", label: "Meu Perfil", icon: Settings, roles: ["CUSTOMER", "MERCHANT", "ADMIN"] },
    { to: "/merchant", label: "Meu Negócio", icon: Store, roles: ["MERCHANT", "ADMIN"] },
    { to: "/admin", label: "Painel Admin", icon: Shield, roles: ["ADMIN"] },
    { to: "/admin/companies", label: "Empresas", icon: ShoppingBag, roles: ["ADMIN"] },
    { to: "/admin/company-categories", label: "Categorias", icon: Tag, roles: ["ADMIN"] },
    { to: "/admin/segments", label: "Segmentos", icon: Compass, roles: ["ADMIN"] },
    { to: "/admin/blog", label: "Blog (Admin)", icon: FileText, roles: ["ADMIN"] },
    { to: "/admin/cities", label: "Cidades (Admin)", icon: MapPin, roles: ["ADMIN"] },
    { to: "/admin/plans", label: "Planos", icon: Tag, roles: ["ADMIN"] },
    { to: "/admin/highlights", label: "Destaques", icon: Star, roles: ["ADMIN"] },
    { to: "/admin/slides", label: "Slides", icon: ImageIcon, roles: ["ADMIN"] },
    { to: "/admin/home-sections", label: "Ordem da Home", icon: ImageIcon, roles: ["ADMIN"] },
    { to: "/admin/quick-register", label: "Cadastro Rápido", icon: Compass, roles: ["ADMIN"] },
    { to: "/admin/osm-search", label: "Buscar OSM", icon: Globe, roles: ["ADMIN"] },
    { to: "/admin/wallets", label: "Carteiras", icon: Wallet, roles: ["ADMIN"] },
    { to: "/admin/report", label: "Relatórios", icon: FileText, roles: ["ADMIN"] },
    { to: "/admin/notifications", label: "Notificações", icon: Settings, roles: ["ADMIN"] },
    { to: "/admin/settings", label: "Config.", icon: Settings, roles: ["ADMIN"] },
  ];

  const visible = navItems.filter((n) => {
    if (n.public) return true;
    if (!user) return n.publicForAnon === true;
    if (n.roles?.includes(user.role)) return true;
    if (n.anyRole?.includes(user.role)) return true;
    return false;
  });

  // Desktop admin dropdown groups
  const adminGroups = [
    { label: "Conteúdo", items: [
      { to: "/admin/slides", label: "Slides", icon: ImageIcon },
      { to: "/admin/home-sections", label: "Ordem da Home", icon: ImageIcon },
      { to: "/admin/highlights", label: "Destaques", icon: Star },
      { to: "/admin/blog", label: "Blog", icon: FileText },
      { to: "/admin/segments", label: "Segmentos", icon: Compass },
    ]},
    { label: "Cadastros", items: [
      { to: "/admin/users", label: "Usuários", icon: Users },
      { to: "/admin/pending-merchants", label: "Aprovar Comerciantes", icon: CheckCircle2 },
      { to: "/admin/companies", label: "Empresas", icon: Building2 },
      { to: "/admin/company-categories", label: "Categorias de Empresa", icon: Tag },
      { to: "/admin/cities", label: "Cidades", icon: MapPin },
      { to: "/admin/quick-register", label: "Cadastro Rápido", icon: Compass },
      { to: "/admin/osm-search", label: "Buscar OSM", icon: Globe },
    ]},
    { label: "Financeiro", items: [
      { to: "/admin/plans", label: "Planos", icon: Tag },
      { to: "/admin/wallets", label: "Carteiras", icon: Wallet },
      { to: "/admin/payouts", label: "Resgates PIX", icon: Landmark },
      { to: "/admin/report", label: "Relatórios", icon: FileText },
    ]},
    { label: "Sistema", items: [
      { to: "/admin/notifications", label: "Notificações", icon: Settings },
      { to: "/admin/settings", label: "Configurações", icon: Settings },
    ]},
  ];

  const linkCls = ({ isActive }) => `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? "text-blue-950 bg-blue-50" : "text-stone-600 hover:text-stone-900 hover:bg-stone-100"}`;

  return (
    <>
      <header
        className="sticky top-0 z-50 w-full backdrop-blur-xl bg-white/85 border-b border-stone-200/60 shadow-sm"
        data-testid="app-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setOpen(true)}
              className="md:hidden text-stone-700"
              data-testid="mobile-menu-btn"
              aria-label="Abrir menu"
            >
              <Menu size={22} />
            </Button>
            <Link to="/" className="flex items-center" data-testid="logo-home">
              <img src={LOGO_URL} alt="Morar Bem" className="h-10 sm:h-12 w-auto object-contain" />
            </Link>
          </div>

          <nav className="hidden md:flex items-center gap-1">
            <NavLink to="/" end className={linkCls} data-testid="nav-home">Início</NavLink>
            <NavLink to="/discover/all" className={linkCls} data-testid="nav-discover">Descobrir</NavLink>
            <NavLink to="/cidades" className={linkCls} data-testid="nav-cities">Cidades</NavLink>
            <NavLink to="/shop" className={linkCls} data-testid="nav-shop">Shop</NavLink>
            <NavLink to="/blog" className={linkCls} data-testid="nav-blog">Blog</NavLink>
            {(!user || user.role === "ADMIN" || user.role === "MERCHANT") && (
              <NavLink to="/pricing" className={linkCls} data-testid="nav-pricing">Planos</NavLink>
            )}
            {(!user || user.role === "CUSTOMER") && (
              <NavLink to="/para-empresas" className={linkCls} data-testid="nav-business">Cadastrar empresa</NavLink>
            )}
            {user?.role === "CUSTOMER" && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="px-3 py-2 rounded-lg text-sm font-medium text-stone-600 hover:text-stone-900 hover:bg-stone-100 flex items-center gap-1.5 transition-colors"
                    data-testid="nav-customer-trigger"
                  >
                    <Wallet size={16} />Minha Conta <ChevronDown size={14} className="opacity-60"/>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56" data-testid="nav-customer-menu">
                  <DropdownMenuItem onClick={() => navigate("/wallet")} data-testid="cust-menu-wallet">
                    <Wallet size={14} className="mr-2 text-blue-900"/>Carteira BemCash
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/orders")} data-testid="cust-menu-orders">
                    <ShoppingBag size={14} className="mr-2 text-stone-500"/>Minhas Compras
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/vouchers")} data-testid="cust-menu-vouchers">
                    <Tag size={14} className="mr-2 text-stone-500"/>Meus Vouchers
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/favorites")} data-testid="cust-menu-favorites">
                    <Star size={14} className="mr-2 text-stone-500"/>Favoritos
                  </DropdownMenuItem>
                  <DropdownMenuSeparator/>
                  <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="cust-menu-profile">
                    <Settings size={14} className="mr-2 text-stone-500"/>Meu Perfil
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {user?.role === "MERCHANT" && (
              <NavLink to="/merchant" className={({ isActive }) => `${linkCls({ isActive })} flex items-center gap-1.5`} data-testid="nav-merchant"><Store size={16} />Meu Negócio</NavLink>
            )}
            {user?.role === "ADMIN" && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="px-3 py-2 rounded-lg text-sm font-medium text-stone-600 hover:text-stone-900 hover:bg-stone-100 flex items-center gap-1.5 transition-colors"
                    data-testid="nav-admin-trigger"
                  >
                    <Shield size={16} />Admin <ChevronDown size={14} className="opacity-60"/>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64 max-h-[80vh] overflow-y-auto" data-testid="nav-admin-menu">
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="adm-menu-dashboard">
                    <Shield size={14} className="mr-2 text-blue-900"/>Painel Admin
                  </DropdownMenuItem>
                  <DropdownMenuSeparator/>
                  {adminGroups.map((g) => (
                    <React.Fragment key={g.label}>
                      <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-stone-400 font-bold">{g.label}</DropdownMenuLabel>
                      {g.items.map((it) => (
                        <DropdownMenuItem key={it.to} onClick={() => navigate(it.to)} data-testid={`adm-menu-${it.to.replace(/\//g, "-")}`}>
                          <it.icon size={14} className="mr-2 text-stone-500"/>{it.label}
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator/>
                    </React.Fragment>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </nav>

          <div className="flex items-center gap-2">
            {(!user || user.role === "CUSTOMER") && itemCount > 0 && (
              <Link to="/checkout" className="relative inline-flex items-center justify-center w-9 h-9 rounded-xl text-stone-700 hover:bg-stone-100" data-testid="cart-icon" title="Carrinho">
                <ShoppingCart size={18}/>
                <span className="absolute -top-1 -right-1 bg-amber-500 text-amber-950 text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center" data-testid="cart-count">{itemCount}</span>
              </Link>
            )}
            {user ? (
              <>
                <Link to="/profile" className="hidden sm:flex flex-col items-end leading-tight hover:opacity-80 transition" data-testid="header-profile-link" title="Meu perfil">
                  <span className="text-sm font-medium text-stone-800" data-testid="user-name">{user.name}</span>
                  <span className="text-[10px] uppercase tracking-wider text-stone-500">{user.role}</span>
                </Link>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={async () => { await logout(); navigate("/"); }}
                  className="text-stone-700 hover:text-stone-900"
                  data-testid="header-logout"
                >
                  <LogOut size={16} />
                  <span className="hidden sm:inline ml-1.5 text-xs">Sair</span>
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={() => navigate("/login")} data-testid="header-login-btn">
                  <LogIn size={16} className="mr-1.5" /> Entrar
                </Button>
                <Button size="sm" onClick={() => navigate("/register")}
                  className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl shadow-md shadow-blue-950/20" data-testid="header-register-btn">
                  Criar conta
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* MOBILE DRAWER */}
      {open && (
        <div className="fixed inset-0 z-[100] md:hidden" data-testid="mobile-menu-drawer">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={close} />
          <aside className="absolute left-0 top-0 bottom-0 w-[82%] max-w-sm bg-white shadow-2xl flex flex-col animate-in slide-in-from-left">
            <div className="flex items-center justify-between p-4 border-b border-stone-200">
              <img src={LOGO_URL} alt="Morar Bem" className="h-10 object-contain" />
              <Button variant="ghost" size="icon" onClick={close} data-testid="mobile-menu-close">
                <X size={20} />
              </Button>
            </div>
            {user && (
              <div className="px-4 py-3 bg-blue-50 border-b border-blue-100">
                <div className="text-sm font-semibold text-blue-950">{user.name}</div>
                <div className="text-[11px] text-blue-800 uppercase tracking-wider">{user.role}</div>
                {user.wallet_code && (
                  <div className="text-[10px] text-stone-600 mt-1 font-mono">Carteira: {user.wallet_code}</div>
                )}
              </div>
            )}
            <nav className="flex-1 overflow-auto py-3">
              {visible.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  onClick={close}
                  className={({ isActive }) => `flex items-center gap-3 px-4 py-3 text-sm font-medium border-l-4 transition-colors ${isActive ? "text-blue-950 bg-blue-50 border-blue-900" : "text-stone-700 border-transparent hover:bg-stone-50"}`}
                  data-testid={`mobile-nav-${n.to.replace(/\//g, "-")}`}
                >
                  <n.icon size={18} />
                  <span>{n.label}</span>
                </NavLink>
              ))}
            </nav>
            <div className="p-4 border-t border-stone-200 space-y-2">
              {user ? (
                <Button onClick={async () => { await logout(); close(); navigate("/"); }} variant="outline" className="w-full rounded-xl" data-testid="mobile-logout">
                  <LogOut size={16} className="mr-2" /> Sair
                </Button>
              ) : (
                <>
                  <Button onClick={() => { close(); navigate("/login"); }} variant="outline" className="w-full rounded-xl" data-testid="mobile-login">
                    <LogIn size={16} className="mr-2" /> Entrar
                  </Button>
                  <Button onClick={() => { close(); navigate("/register"); }} className="w-full bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="mobile-register">
                    Criar conta
                  </Button>
                </>
              )}
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
